import DesktopDatePicker from '@mui/lab/DesktopDatePicker';
import { Button, TextField } from '@mui/material';
import { Box } from '@mui/system';
import { sortBy } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';

import { IOptionType, Table, Page } from '../../components';
import { getNetSuiteLog } from '../../fetch/netSuite';
import { getLocalDate } from '../../helpers';
import { useToastContext } from '../../hooks';
import { INetSuiteLog, INetSuiteLogFilters } from '../../models';
import { NetSuiteLogFilters } from './NetSuiteLogFilters';

const datePickerStyle: React.CSSProperties = {
  width: '12rem',
  fontSize: '14px',
};

const todaysDate = (data: {
  hours: number;
  minutes: number;
  seconds: number;
  ms: number;
}): string => {
  const { hours, minutes, seconds, ms } = data;
  const d = new Date();
  const date = new Date(d.getFullYear(), d.getMonth(), d.getDate(), hours, minutes, seconds, ms);
  return date.toISOString();
};

export const NetSuiteLog = () => {
  const { showToast } = useToastContext();

  const [netSuiteLogData, setNetSuiteLogData] = useState<INetSuiteLog[]>([]);
  const [filteredNetSuiteLogData, setFilteredNetSuiteLogData] = useState<INetSuiteLog[]>([]);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [filters, setFilters] = useState<INetSuiteLogFilters>({});
  const [entityFilterOptions, setEntityFilterOptions] = useState<IOptionType[]>([]);
  const [actionFilterOptions, setActionFilterOptions] = useState<IOptionType[]>([]);
  const [startDate, setStartDate] = useState<string | null>(() =>
    todaysDate({ hours: 0, minutes: 0, seconds: 0, ms: 0 })
  );
  const [endDate, setEndDate] = useState<string | null>(() =>
    todaysDate({ hours: 23, minutes: 59, seconds: 59, ms: 999 })
  );

  const fetchNetSuiteLogs = async () => {
    if (!startDate || !endDate) return;
    setLoading(true);

    try {
      const res = await getNetSuiteLog(getLocalDate(startDate), getLocalDate(endDate));
      const dateSortedLogData = res?.sort((a, b) => {
        return b.creationTimeStamp.localeCompare(a.creationTimeStamp);
      });

      setNetSuiteLogData(dateSortedLogData);
      setFilteredNetSuiteLogData(dateSortedLogData);

      // Set unique filter options for entity dropdown
      const entityFilterOptions: IOptionType[] = [];
      dateSortedLogData.forEach(data => {
        // Skip duplicates
        if (!entityFilterOptions.some(option => option.value === data.entity)) {
          entityFilterOptions.push({ key: data.entity, value: data.entity, label: data.entity });
        }
      });
      setEntityFilterOptions(sortBy(entityFilterOptions, ['label'], ['desc']));

      // Set unique filter options for action dropdown
      const actionFilterOptions: IOptionType[] = [];
      dateSortedLogData.forEach(data => {
        // Skip duplicates
        if (!actionFilterOptions.some(option => option.value === data.action)) {
          actionFilterOptions.push({ key: data.action, value: data.action, label: data.action });
        }
      });
      setActionFilterOptions(sortBy(actionFilterOptions, ['label'], ['desc']));
    } catch (error: any) {
      setNetSuiteLogData([]);
      setFilteredNetSuiteLogData([]);
      showToast(
        'error',
        error?.detail || 'Error trying to retrieve NetSuite logs. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Render todays logs on mount.
  useEffect(() => {
    fetchNetSuiteLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter the table based on... the filters.
  useEffect(() => {
    if (!filters) return;

    let filteredData = netSuiteLogData;

    if (filters.invoiceNumber !== '') {
      filteredData = filteredData.filter(logEntry =>
        logEntry?.invoiceNumber
          ?.toString()
          .toLowerCase()
          .includes(filters?.invoiceNumber?.toLowerCase() as string)
      );
    }

    if (filters.netsuiteReference !== '') {
      filteredData = filteredData.filter(logEntry =>
        logEntry?.netsuiteId?.includes(filters.netsuiteReference as string)
      );
    }

    if (filters.entity) {
      filteredData = filteredData.filter(
        logEntry => logEntry?.entity?.toLowerCase() === filters.entity?.toLowerCase()
      );
    }

    if (filters.action) {
      filteredData = filteredData.filter(
        logEntry => logEntry?.action?.toLowerCase() === filters.action?.toLowerCase()
      );
    }

    if (filters.account !== '') {
      filteredData = filteredData.filter(logEntry =>
        logEntry?.account?.toLowerCase().includes(filters?.account?.toLowerCase() as string)
      );
    }

    setFilteredNetSuiteLogData(filteredData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const columns = useMemo(() => {
    return [
      {
        accessor: 'invoiceNumber',
        Header: 'Invoice',
        Cell: ({
          cell: {
            row: { original },
          },
        }: {
          cell: { row: { original: INetSuiteLog } };
        }) => {
          return <a href={original.cbsHyperlink}>{original.invoiceNumber}</a>;
        },
      },
      {
        accessor: 'netsuiteId',
        Header: 'NetSuite Reference',
      },
      {
        accessor: 'entity',
        Header: 'Entity',
      },
      {
        accessor: 'action',
        Header: 'Action',
      },
      {
        accessor: 'account',
        Header: 'Account',
      },
      {
        accessor: 'entityName',
        Header: 'Entity Name',
      },
      {
        accessor: 'debit',
        Header: 'Debit',
        isCurrency: true,
      },
      {
        accessor: 'credit',
        Header: 'Credit',
        isCurrency: true,
      },
      {
        accessor: 'user',
        Header: 'User',
      },
      {
        accessor: 'creationTimeStamp',
        Header: 'Date/Time',
        isDateTime: true,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Page title="NetSuite Log">
      <Box
        sx={theme => ({
          // Let the container take care of consistent spacing between elements.
          '& > *:not(:last-child)': {
            marginBottom: theme.spacing(1),
          },
        })}
      >
        {/* DATE RANGE SEARCH */}
        <Box
          sx={theme => ({
            '& > *:not(:last-child)': {
              marginRight: theme.spacing(1),
              [theme.breakpoints.down('sm')]: {
                marginBottom: theme.spacing(1),
              },
            },
          })}
        >
          {/* START DATE */}
          <DesktopDatePicker
            label="Start Date"
            inputFormat="MM/dd/yyyy"
            value={startDate}
            onChange={date => setStartDate(date)}
            renderInput={params => <TextField {...params} />}
            InputProps={{ style: datePickerStyle }}
          />

          {/* END DATE */}
          <DesktopDatePicker
            label="End Date"
            inputFormat="MM/dd/yyyy"
            value={endDate}
            onChange={date => setEndDate(date)}
            renderInput={params => <TextField {...params} />}
            InputProps={{ style: datePickerStyle }}
          />

          {/* SEARCH BUTTON */}
          <Button
            color="secondary"
            variant="contained"
            onClick={() => fetchNetSuiteLogs()}
            disabled={!startDate || !endDate}
          >
            Search
          </Button>
        </Box>

        {/* FILTER BAR */}
        <NetSuiteLogFilters
          handleFilter={setFilters}
          isLoading={isLoading}
          entityFilterOptions={entityFilterOptions}
          actionFilterOptions={actionFilterOptions}
        />

        {/* GRID */}
        <Box
          display="flex"
          width="100%"
          flexDirection="column"
          alignItems="center"
          sx={{
            overflowY: 'hidden',
            '.MuiTable-stickyHeader .MuiTableHead-root': {
              top: 0,
            },
            '.MuiTableContainer-root': {
              overflowX: 'auto',
              maxHeight: '60vh',
            },
          }}
        >
          <Table
            columns={columns}
            data={filteredNetSuiteLogData}
            isLoading={isLoading}
            stickyHeader
          />
        </Box>
      </Box>
    </Page>
  );
};
